import { env } from '@/config/env'
import { AuthUser__UserFragment as User } from '@/graphql/api/generated'

import { ChatwootService } from './chatwoot'
import { CrispService } from './crisp'
import { ChatServiceType, IChatService } from './interfaces'
import { PostHogService } from './posthog'
import { SentryService } from './sentry'

export { ChatwootService } from './chatwoot'
export { CrispService } from './crisp'
export { PostHogService } from './posthog'
export { SentryService } from './sentry'

type EventProperties = Record<
  string,
  string | number | boolean | null | undefined
>

export class TrackingService {
  private static activeChatService: IChatService | null = null
  private static activeChatServiceType: ChatServiceType | null = null
  private static isInitialized = false

  static init(): void {
    this.initializeChatService()
  }

  static initializeChatService(): IChatService | null {
    if (this.isInitialized) return this.activeChatService

    const chatServices = [
      {
        type: ChatServiceType.Chatwoot as const,
        baseUrlVar: env.CHATWOOT_BASE_URL,
        tokenVar: env.CHATWOOT_WEBSITE_TOKEN,
        create: (baseUrl: string, token: string) =>
          new ChatwootService(baseUrl, token),
      },
      {
        type: ChatServiceType.Crisp as const,
        baseUrlVar: null,
        tokenVar: env.CRISP_WEBSITE_ID,
        create: () => new CrispService(),
      },
    ]

    const availableService = chatServices.find((service) => {
      if (service.type === ChatServiceType.Chatwoot) {
        return service.baseUrlVar && service.tokenVar
      }
      if (service.type === ChatServiceType.Crisp) {
        return service.tokenVar
      }
      return false
    })

    if (availableService) {
      if (availableService.type === ChatServiceType.Chatwoot) {
        this.activeChatService = availableService.create(
          availableService.baseUrlVar!,
          availableService.tokenVar!
        )
      } else {
        this.activeChatService = availableService.create()
        // Init chat service when needed
        if (this.activeChatService.init) {
          this.activeChatService.init()
        }
      }
      this.activeChatServiceType = availableService.type
    }

    this.isInitialized = true
    return this.activeChatService
  }

  static setUser(user?: User): void {
    if (!this.isInitialized) {
      this.initializeChatService()
    }

    if (user) {
      PostHogService.identify(user)
      SentryService.setUser(user)

      if (this.activeChatService?.setUser) {
        this.activeChatService.setUser(user)
      }
    }
  }

  static reset(): void {
    PostHogService.reset()
    SentryService.clearUser()

    if (this.activeChatService) {
      this.activeChatService.reset()
    }
  }

  static captureEvent(event: string, properties?: EventProperties): void {
    PostHogService.capture(event, properties)
  }

  static captureException(error: Error): void {
    SentryService.captureException(error)
  }

  static toggleChat(state?: 'open' | 'close'): void {
    if (!this.isInitialized) {
      this.initializeChatService()
    }

    if (this.activeChatService && this.activeChatService.toggle) {
      this.activeChatService.toggle(state)
    }
  }

  static getActiveChatService(): IChatService | null {
    return this.activeChatService
  }

  static getActiveChatServiceType(): ChatServiceType | null {
    return this.activeChatServiceType
  }

  static isReady(): boolean {
    return this.isInitialized && this.activeChatService !== null
  }
}
